'use client';

import { useMemo, useState } from 'react';
import type { ExternalNetworkDto } from '@terra/shared-contracts-external-network';
import {
  ExternalNetworkExecutionSettings,
  Tabs,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useActiveExternalNetworkScan } from '../../../../hooks/api/useExternalNetworkScans';
import { SettingsSubTabs } from '../../layout/SettingsSubTabs';
import { ExternalScanningSettingsPanel } from './ExternalScanningSettingsPanel';
import { usePersistExternalNetworkSettings } from './usePersistExternalNetworkSettings';

const AUTOMATION_SUB_TAB = {
  scan: 'scan',
  execution: 'execution',
} as const;

type AutomationSubTab =
  (typeof AUTOMATION_SUB_TAB)[keyof typeof AUTOMATION_SUB_TAB];

type ExternalAutomationsSettingsPanelProps = {
  surface: ExternalNetworkDto;
  surfaceId: string;
  isReadOnly: boolean;
};

export function ExternalAutomationsSettingsPanel({
  surface,
  surfaceId,
  isReadOnly,
}: ExternalAutomationsSettingsPanelProps) {
  const tAutomations = useTranslations('externalNetwork.settings.automations');
  const tExecutions = useTranslations('designSystem.automation.executions');
  const [activeSubTab, setActiveSubTab] = useState<AutomationSubTab>(
    AUTOMATION_SUB_TAB.scan,
  );
  const { activeScan } = useActiveExternalNetworkScan({ surfaceId });
  const { isUpdating, persist } = usePersistExternalNetworkSettings({
    surfaceId,
    isReadOnly,
  });
  const subTabs = useMemo(
    () => [
      { value: AUTOMATION_SUB_TAB.scan, label: tAutomations('tabs.scan') },
      {
        value: AUTOMATION_SUB_TAB.execution,
        label: tExecutions('sectionTitle'),
      },
    ],
    [tAutomations, tExecutions],
  );

  return (
    <SettingsSubTabs
      value={activeSubTab}
      onValueChange={setActiveSubTab}
      items={subTabs}
    >
      <Tabs.Content value={AUTOMATION_SUB_TAB.scan}>
        <ExternalScanningSettingsPanel
          surface={surface}
          surfaceId={surfaceId}
          isReadOnly={isReadOnly}
          isScanActive={Boolean(activeScan)}
        />
      </Tabs.Content>

      <Tabs.Content value={AUTOMATION_SUB_TAB.execution}>
        <ExternalNetworkExecutionSettings
          surface={surface}
          isReadOnly={isReadOnly}
          isUpdating={isUpdating}
          onPersist={persist}
        />
      </Tabs.Content>
    </SettingsSubTabs>
  );
}
